import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../../lib/site";
import { Reveal } from "./motion";
import { VideoPlaceholder, Swoosh } from "./ui";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const rot = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-white pt-32 pb-24 lg:pt-40">
      <Swoosh className="-left-40 top-1/3 h-[520px] w-[520px] text-slate-900/[0.04]" />
      <Swoosh className="-right-40 top-1/2 h-[520px] w-[520px] rotate-180 text-slate-900/[0.04]" />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Photo collage */}
          <div className="relative order-2 h-[440px] sm:h-[560px] lg:order-1 lg:col-span-6">
            <motion.figure
              style={{ y: y1, rotate: rot }}
              className="absolute left-0 top-0 w-[62%] overflow-hidden rounded-lg border-4 border-teal-800 shadow-2xl"
            >
              <img
                src={IMAGES.heroCoast}
                alt="Aerial coastline"
                className="aspect-[4/5] w-full object-cover"
              />
            </motion.figure>

            <motion.figure
              style={{ y: y2 }}
              className="absolute bottom-8 right-0 w-[58%] overflow-hidden rounded-lg border-[6px] border-emerald-500/70 shadow-2xl"
            >
              <img
                src={IMAGES.heroForest}
                alt="Aerial forest"
                className="aspect-[4/3] w-full object-cover"
              />
            </motion.figure>

            <motion.figure
              style={{ y: y1 }}
              className="absolute bottom-0 right-6 w-[42%] overflow-hidden rounded-lg border-4 border-slate-400/60 shadow-2xl"
            >
              <img
                src={IMAGES.transport4x4}
                alt="4x4 vehicles on beach"
                className="aspect-video w-full object-cover"
              />
            </motion.figure>
          </div>

          {/* Intro copy */}
          <div className="order-1 lg:order-2 lg:col-span-6 lg:pl-6">
            <Reveal delay={0.1}>
              <h1 className="font-serif text-4xl text-emerald-600 sm:text-5xl">
                Hello &amp; Welcome
              </h1>
            </Reveal>
            <Reveal delay={0.25} className="mt-8 space-y-6">
              <p className="font-sans text-base leading-relaxed text-slate-600 sm:text-lg">
                Having always wanted to start my own business and having a passion
                for drone photography &amp; reconnaissance, I managed to combine the
                two in <span className="italic text-slate-900">Birds Eye Views</span>. My
                name is George, I'm 20 and I am a proficient, safe and reliable drone
                pilot with an eye for detail.
              </p>
              <p className="font-sans text-base leading-relaxed text-slate-600 sm:text-lg">
                If you or your business need a little extra stylish perspective on
                your home, farm, or even your wedding, supplied as a complete edited
                video, or just if you need an eye in the sky to help you find that
                leak in the roof or a lost dog, please see my short showreel and get
                in touch.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Showreel video placeholder */}
        <Reveal delay={0.2} className="relative mx-auto mt-20 max-w-4xl">
          <VideoPlaceholder testId="showreel-placeholder" vimeoId="1162604782" className="relative z-10" />
        </Reveal>
      </div>
    </section>
  );
};
